<template>
  <v-container grid-list-md>
    <v-layout row wrap>
      <v-flex xs8 offset-xs2 my-2>
        <p
          v-if="this.$store.getters.getDealerApplication.status >= 1"
          class="text-xs-center subheading"
        >
          {{ $t('message.instructions.finalizeSubmitted') }}
        </p>
        <p v-else class="text-xs-center subheading">
          {{ $t('message.instructions.finalize') }}
        </p>
      </v-flex>
    </v-layout>

    <v-divider></v-divider>
    <v-layout row wrap>
      <!-- Application Section -->
      <v-container grid-list-md ma-0 pa-0>
        <applicant-view/>

        <v-divider style="margin-top: 20px;"></v-divider>

        <!-- Proof of Business Entity Section -->
        <div>
          <h2>{{ $t('message.proofOfBusEntity') }}</h2>
          <v-layout
            row
            wrap
            v-if="this.$store.getters.getIncArticles.length > 0"
          >
            <v-flex
              v-for="incArticle in this.$store.getters.getIncArticles"
              xs12
              sm6
              md4
              lg3
              :key="incArticle.id"
            >
              <v-card>
                <template>
                  <v-container grid-list-md>
                    <v-layout row wrap>
                      <v-flex>
                        <h4 class="gray--text">
                          {{ incArticle.incCertificationFilename }}
                        </h4>
                        {{ formatDate(incArticle.lastUpdateDate) }}
                      </v-flex>
                    </v-layout>
                  </v-container>
                </template>
              </v-card>
            </v-flex>
          </v-layout>

          <p
            v-if="this.$store.getters.getIncArticles.length == 0"
            v-html="$t('message.finalize.noIncArticle')"
          ></p>
        </div>
        <v-divider style="margin-top: 20px;"></v-divider>

        <!-- Owners Section -->
        <div>
          <h2>{{ $t('message.tabHeader.owners') }}</h2>
          <v-layout row wrap v-if="this.$store.getters.getOwners.length > 0">
            <v-flex
              v-for="owner in this.$store.getters.getOwners"
              xs12
              sm6
              md4
              lg3
              :key="owner.emailAddress"
            >
              <v-card>
                <template>
                  <v-container grid-list-md>
                    <v-layout row wrap>
                      <v-flex>
                        <h4 class="gray--text">{{ owner.name }}</h4>
                        {{ owner.emailAddress }}
                      </v-flex>
                    </v-layout>
                  </v-container>
                </template>
              </v-card>
            </v-flex>
          </v-layout>

          <p
            v-if="this.$store.getters.getOwners.length == 0"
            v-html="$t('message.finalize.noOwner')"
          ></p>
        </div>

        <v-divider style="margin-top: 20px;"></v-divider>
        <!-- Location Section -->
        <h2>{{ $t('message.tabHeader.storeLocations') }}</h2>
        <v-container
          grid-list-md
          v-if="this.$store.getters.getLocations.length > 0"
        >
          <v-data-table
            :headers="locationHeaders"
            :items="this.$store.getters.getLocations"
            hide-actions
            class="elevation-1"
          >
            <template slot="items" slot-scope="props">
              <td>{{ props.item.name }}</td>
              <td class="hidden-sm-and-down">{{ props.item.addressStreet }}</td>
              <td>{{ props.item.addressCity }}</td>
              <td class="hidden-xs-and-down">{{ props.item.addressState }}</td>
              <td class="hidden-sm-and-down">
                {{ props.item.addressZipCode }}
              </td>
              <td class="hidden-sm-and-down">
                {{ props.item.phoneNumber | phoneFilter }}
              </td>
            </template>
          </v-data-table>
        </v-container>
        <p v-if="this.$store.getters.getLocations.length == 0">
          {{ $t('message.finalize.noLocation') }}
        </p>

        <!-- ACH Section -->
        <v-container v-if="this.$store.getters.isACHRequired">
          <v-container
            px-0
            grid-list-md
            v-if="this.$store.getters.getAchAccounts.length > 0"
          >
            <v-divider style="margin-top: 20px;"></v-divider>
            <h2>{{ $t('message.tabHeader.achInfo') }}</h2>

            <v-flex mt-3>
              <span v-html="$t('message.achTermsPleaseClick')"></span>

              <a href="#" target="_blank" @click.prevent="downloadAchTerms()"
                >AchTerms.pdf</a
              >
            </v-flex>

            <v-container grid-list-md>
              <v-data-table
                :headers="achHeaders"
                :items="this.$store.getters.getAchAccounts"
                hide-actions
                class="elevation-1"
              >
                <template slot="headerCell" slot-scope="props">
                  <span>
                    <img
                      src="static/icons/remove_red_eye_black.png"
                      width="32"
                      v-if="props.header.value == 'accountNumber'"
                      @click="showBankAccount = !showBankAccount"
                    />
                    {{ props.header.text }}
                  </span>
                </template>

                <template slot="items" slot-scope="props">
                  <td>{{ props.item.bankName }}</td>
                  <td class="hidden-sm-and-down">
                    {{ props.item.routingNumber }}
                  </td>
                  <td>
                    <div v-show="showBankAccount">
                      {{ props.item.accountNumber }}
                    </div>
                    <div v-show="!showBankAccount">
                      {{ props.item.accountNumber | maskedNumberFilter }}
                    </div>
                  </td>
                  <td v-if="props.item.docFilename">
                    {{ props.item.docFilename }}
                  </td>
                  <td v-else>
                    Please upload a voided check
                  </td>
                </template>
              </v-data-table>
            </v-container>
          </v-container>

          <v-container px-0 grid-list-md v-else>
            <v-divider style="margin-top: 20px;"></v-divider>
            <h2>{{ $t('message.tabHeader.achInfo') }}</h2>
            ACH required
          </v-container>
        </v-container>

        <!-- ESign Section -->
        <v-container
          grid-list-md
          class="elevation-6 e-sign-card"
          v-if="this.showESign()"
        >
          <h3>Dealer Authorization, Release, and Agreement</h3>
          <p class="pb-3">
            In this disclosure agreement, the term “Dealer” refers to the
            organization identified in the APPLICANT section of the application
            (and not the persons identified in the OWNERS section). The term
            “Beneficial Owner” refers to the individuals with at least 25%
            ownership control of the entity which are identified in the OWNERS
            section.
          </p>
          <hr />
          <LenderSelectionEsign
            class="lender-selection"
            @change="setAllLenderBoxesChecked"
            @all-terms-downloaded="setAllLenderTermsDownloaded"
          />

          <v-layout row wrap>
            <v-form
              ref="esignForm"
              @submit.prevent="esignApplication"
              v-show="!this.$store.getters.loading"
              @keydown="errors.clear($event.target.name)"
            >
              <v-flex sm12>
                <v-text-field
                  v-model.trim="esignature"
                  :rules="esignatureRules"
                  :label="$t('message.fullName')"
                  validate-on-blur
                  :disabled="
                    !this.$store.getters.isDealerApplicationComplete(false)
                  "
                  required
                ></v-text-field>
              </v-flex>
            </v-form>
          </v-layout>
        </v-container>

        <v-container v-else-if="this.shouldShowAppNotReadyMessage()">
          <div class="red darken-4 white--text">
            <h2 class="text-xs-center">
              {{ appNotReadyMessage() }}
            </h2>
          </div>
        </v-container>

      </v-container>

      <v-container grid-list-md>
        <v-flex sm8 offset-sm2 v-if="this.showSubmit()">
          <v-divider style="margin-top: 20px;"></v-divider>
          <p class="text-xs-center" v-html="$t('message.appSubmitTerms')"></p>

          <v-divider style="margin-top: 20px;"></v-divider>
          <p
            class="text-xs-center"
            v-html="
              $t('message.finalizeDaysRemaining', {
                expirationDate: this.formatDateOnly(
                  this.$store.getters.getDealerApplication.expirationDate
                )
              })
            "
          ></p>
        </v-flex>
        <!-- Submit Button -->
        <v-flex sm4 offset-sm4 class="button-buffer">
          <v-btn
            block
            class="btn-primary"
            @click="submitApplication"
            v-if="this.showSubmit()"
            :disabled="this.actionButtonDisabled()"
            >{{ $t('message.submitApplication') }}</v-btn
          >
          <v-btn
            block
            class="btn-primary"
            @click="esignApplication"
            v-if="this.showESign()"
            :disabled="this.actionButtonDisabled()"
            >{{ $t('message.esignApplication') }}</v-btn
          >
          <v-btn
            block
            class="btn-primary"
            @click="unlockApplication"
            v-if="this.showUnlock()"
            :disabled="this.actionButtonDisabled()"
            >{{ $t('message.unlockApplication') }}</v-btn
          >
        </v-flex>
      </v-container>
    </v-layout>
  </v-container>
</template>

<script>
import LenderSelectionEsign from './LenderSelectionEsign'
import { axiosAuthInstance } from '../../main.js'
import LoadingModal from '../util/LoadingModal'
import ApplicantView from './ApplicantView'
import { APP_STATUSES } from '../../main.js'

export default {
  data: () => ({
    lenderTermsPdfDownloaded: false,
    allLenderBoxesChecked: false,
    locationHeaders: [
      { text: 'Name', align: 'left', value: 'name', sortable: false },
      {
        text: 'Street Address',
        value: 'addressStreet',
        class: 'hidden-sm-and-down',
        sortable: false
      },
      { text: 'City', value: 'addressCity', sortable: false },
      {
        text: 'State',
        value: 'addressState',
        class: 'hidden-xs-and-down',
        sortable: false
      },
      {
        text: 'Zip Code',
        value: 'addressZipCode',
        class: 'hidden-sm-and-down',
        sortable: false
      },
      {
        text: 'Phone',
        value: 'phoneNumber',
        class: 'hidden-sm-and-down',
        sortable: false
      }
    ],
    achHeaders: [
      { text: 'Bank Name', value: 'bankName', sortable: false },
      {
        text: 'Routing Number',
        value: 'routingNumber',
        class: 'hidden-sm-and-down',
        sortable: false
      },
      { text: 'Account Number', value: 'accountNumber', sortable: false },
      { text: 'Uploaded File', value: 'uploadedFile', sortable: false }
    ],
    esignature: null,
    esignatureRules: [v => !!v || 'Full Name is required'],
    showBankAccount: false
  }),
  props: ['applicationId'],
  methods: {
    setAllLenderBoxesChecked(value) {
      this.allLenderBoxesChecked = value
    },
    setAllLenderTermsDownloaded() {
      this.lenderTermsPdfDownloaded = true
    },
    actionButtonDisabled() {
      if (this.$store.getters.loading) return true

      if (this.showUnlock()) {
        return false
      }

      //disable if anything invalid
      if (!this.$store.getters.isDealerApplicationComplete(false)) {
        return true
      }

      //check esign
      if (this.showESign()) {
        return !this.esignature
      } else {
        //then it must be SUBMIT
        return !this.$store.getters.isDealerApplicationComplete(true)
      }
    },
    submitApplication() {
      var didConfirm = confirm(
        'Once you click SUBMIT APPLICATION, you are no longer able to edit any information. Please review and ensure all information and documentation provided is accurate.'
      )
      if (didConfirm) {
        this.$store.commit('setLoading', true)
        axiosAuthInstance
          .post(
            'api/v1/dealerApp/submit/' + this.$store.getters.getApplicationId
          )
          .then(response => {
            this.$store.commit('setUnsavedData', false)
            this.$store.getters.getDealerApplication.status =
              APP_STATUSES.SUBMITTED
            this.notifyInfo('Application submitted')

            this.$router.push('/submittedApplication')
          })
          .catch(error => {
            this.notifyAxiosError(error, 'Submit failed')
          })
          .then(() => {
            this.$store.commit('setLoading', false)
          })
      }
    },
    unlockApplication() {
      if (confirm('Are you sure you want to unlock this application?')) {
        this.$store.commit('setLoading', true)
        axiosAuthInstance
          .post(
            'api/v1/dealerApp/unlock/' + this.$store.getters.getApplicationId
          )
          .then(response => {
            this.$store.commit('setDealerApplication', response.data)

            this.notifyInfo('Application Unlocked')

            this.$router.push('/application')
          })
          .catch(error => {
            this.notifyAxiosError(error, 'Unlock failed')
          })
          .then(() => {
            this.$store.commit('setLoading', false)
          })
      }
    },
    esignApplication() {
      if (!this.lenderTermsPdfDownloaded) {
        if (!this.allLenderBoxesChecked) {
          this.notifyWarn(
            'Please click the link and review all Terms and Conditions and check all boxes prior to E-Signing'
          )
        } else {
          this.notifyWarn(
            'Please click the link and review all Terms and Conditions prior to E-Signing'
          )
        }
        return
      }
      if (!this.allLenderBoxesChecked) {
        this.notifyWarn('Please check all boxes prior to E-Signing')
        return
      }

      if (this.$refs.esignForm.validate()) {
        var tempOwner = this.$store.getters.getFirstBeneficialOwner
        this.$store.commit('setLoading', true)

        tempOwner.esignature = this.esignature
        axiosAuthInstance
          .post('api/v1/owner/esign', tempOwner)
          .then(response => {
            tempOwner.status = 'ESIGNED'
            this.notifyInfo('Application E-Signed')
          })
          .catch(error => {
            this.notifyAxiosError(error, 'Esign failed')
          })
          .then(() => {
            this.$store.commit('setLoading', false)
          })
      }
    },
    showESign() {
      if (this.showUnlock()) return false

      if (!this.$store.getters.isDealerApplicationComplete(false)) {
        return false
      }

      if (this.$store.getters.getFirstBeneficialOwner) {
        var result =
          this.$store.getters.getFirstBeneficialOwner.esignature == null
        return result
      } else {
        return false
      }
    },
    isEsignedByAnyBeneficialOwner() {
      return this.$store.getters.getFirstBeneficialOwnerStatus == 'ESIGNED'
    },
    shouldShowAppNotReadyMessage() {
      //don't show msg if showing "unlock" since app state must be good in this case
      if (this.showUnlock()) return false

      //if btn is disabled, something is missing - show 'Not Ready' msg
      return this.actionButtonDisabled()
    },
    appNotReadyMessage() {
      let isEsignNeeded = !this.isEsignedByAnyBeneficialOwner()

      let isAppCompleted = this.$store.getters.isDealerApplicationComplete(
        false
      )
      let msg = 'This application is not ready to be'
      if (isEsignNeeded && !isAppCompleted) {
        msg +=
          ' e-signed because' +
          ' not all application requirements have been met. Please review all tabs that are not marked' +
          ' with a check mark as completed.'
      } else {
        msg +=
          ' submitted because' +
          ' not all application requirements have been met. Please review all tabs that are not marked' +
          ' with a check mark as completed.'
      }
      return msg
    },
    showSubmit() {
      if (this.showUnlock()) return false

      return !this.showESign()
    },
    showUnlock() {
      return (
        this.$store.getters.getDealerApplication.status >= APP_STATUSES.SUBMITTED
      )
    },
    fetchAchAccounts() {
      if (this.$store.getters.getAchAccounts.length == 0) {
        axiosAuthInstance
          .get('api/v1/ach/application/' + this.$store.getters.getApplicationId)
          .then(response => {
            if (response.data && response.data.length > 0) {
              this.$store.commit('setAchAccounts', response.data)
            }
          })
          .catch(error => {
            this.notifyAxiosError(error, 'Failed to fetch ACH data')
          })
      }
    },
    fetchOwners() {
      if (this.$store.getters.getOwners.length == 0) {
        axiosAuthInstance
          .get(
            'api/v1/owner/application/' + this.$store.getters.getApplicationId
          )
          .then(response => {
            if (response.data && response.data.length > 0) {
              this.$store.commit('setOwners', response.data)
            }
          })
          .catch(error => {
            this.notifyAxiosError(
              error,
              'There was an error retrieving the owners '
            )
          })
      }
    },
    fetchStoreLocations() {
      if (this.$store.getters.getLocations.length == 0) {
        axiosAuthInstance
          .get(
            'api/v1/location/application/' +
              this.$store.getters.getApplicationId
          )
          .then(response => {
            if (response.data && response.data.length > 0) {
              this.$store.commit('setLocations', response.data)
            }
          })
          .catch(error => {
            console.log('error response: ' + error)
          })
      }
    }
  },
  mounted() {
    this.fetchAchAccounts()
    this.fetchOwners()
    this.fetchStoreLocations()
  },
  components: {
    'loading-modal': LoadingModal,
    'applicant-view': ApplicantView,
    LenderSelectionEsign
  }
}
</script>
<style lang="scss" scoped>
.checkbox {
  margin-top: 1rem;
}

.lender-signature {
  margin-bottom: 3rem;
}
.lender-selection {
  padding: 1em;
  padding-top: 3em;
}
.e-sign-card {
  padding: 3em;
}
#full-name {
  border-bottom: 1px solid black;
  width: 100%;
  padding: 10px;
  margin-top: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/components/Application/Finalize.vue